/**
 * logoPalette — infer a 4-colour brand palette from an uploaded logo, entirely
 * client-side (canvas pixel sampling). Used by the brand-kit modal to pre-fill
 * the colour pickers the moment a logo is dropped in — the user can still tweak
 * every swatch afterwards.
 *
 * We run on the LOCAL File (object URL) so the canvas is never tainted by CORS,
 * unlike the hosted logo URL.
 */
export interface InferredPalette {
  primary_color: string;
  secondary_color: string;
  tertiary_color: string;
  accent_color: string;
}

type Swatch = { rgb: [number, number, number]; n: number; s: number; l: number };

const clamp = (x: number) => Math.max(0, Math.min(255, Math.round(x)));
const toHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((x) => clamp(x).toString(16).padStart(2, '0')).join('').toUpperCase();

function rgbToSL(r: number, g: number, b: number): { s: number; l: number } {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : l > 0.5 ? d / (2 - max - min) : d / (max + min);
  return { s, l };
}

const dist = (a: [number, number, number], b: [number, number, number]) =>
  Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function inferPaletteFromLogo(file: File): Promise<InferredPalette | null> {
  let url: string | null = null;
  try {
    url = URL.createObjectURL(file);
    const img = await loadImage(url);
    // Downscale for speed; ~96px on the long edge is plenty for dominant colours.
    const scale = Math.min(1, 96 / Math.max(img.width || 1, img.height || 1));
    const w = Math.max(1, Math.round((img.width || 1) * scale));
    const h = Math.max(1, Math.round((img.height || 1) * scale));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    // Quantise to a 16³ grid and accumulate average colour per bucket.
    const buckets = new Map<string, { sum: [number, number, number]; n: number }>();
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 130) continue; // skip transparent (logo backgrounds)
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const key = `${r >> 4},${g >> 4},${b >> 4}`;
      const cur = buckets.get(key);
      if (cur) { cur.sum[0] += r; cur.sum[1] += g; cur.sum[2] += b; cur.n++; }
      else buckets.set(key, { sum: [r, g, b], n: 1 });
    }
    if (!buckets.size) return null;

    let swatches: Swatch[] = [...buckets.values()].map((e) => {
      const rgb: [number, number, number] = [
        Math.round(e.sum[0] / e.n), Math.round(e.sum[1] / e.n), Math.round(e.sum[2] / e.n),
      ];
      const { s, l } = rgbToSL(rgb[0], rgb[1], rgb[2]);
      return { rgb, n: e.n, s, l };
    });

    // Merge near-identical colours (keeps the palette varied).
    const merged: Swatch[] = [];
    for (const sw of swatches.sort((a, b) => b.n - a.n)) {
      const hit = merged.find((m) => dist(m.rgb, sw.rgb) < 38);
      if (hit) hit.n += sw.n; else merged.push({ ...sw });
    }
    swatches = merged;
    const byFreq = [...swatches].sort((a, b) => b.n - a.n);

    // primary  — the dominant brand colour (avoid pure white/black extremes).
    const primary = byFreq.find((e) => e.l > 0.12 && e.l < 0.92) || byFreq[0];
    // accent   — the most vivid colour, distinct from primary.
    const accent = [...swatches].filter((e) => e !== primary).sort((a, b) => b.s - a.s)[0] || primary;
    // secondary — the lightest colour (reads on dark), distinct.
    const secondary = [...swatches].filter((e) => e !== primary && e !== accent).sort((a, b) => b.l - a.l)[0]
      || ({ rgb: [245, 241, 232] } as Swatch);
    // tertiary  — next most frequent distinct colour, else a calm neutral.
    const used = new Set<Swatch>([primary, accent, secondary]);
    const tertiary = byFreq.find((e) => !used.has(e) && dist(e.rgb, primary.rgb) > 30)
      || ({ rgb: [163, 177, 138] } as Swatch);

    return {
      primary_color: toHex(...primary.rgb),
      secondary_color: toHex(...secondary.rgb),
      tertiary_color: toHex(...tertiary.rgb),
      accent_color: toHex(...accent.rgb),
    };
  } catch {
    return null;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}
